import { merge } from 'lodash';
import { useFormButtonOptions, FormAction } from './const';

/**
 * Form element mixin, used for form element widgets
 * A form element is: 'button-like' widget
 * @depends useFormButtonDescriptor()
 * @strat form-button
 */
export const useFormButton = () => ({
    inject: {
        [useFormButtonOptions.inject.FORM]: {
            default: null
        }
    },
    computed: {
        /**
         * @return {Form}
         */
        form() {
            return this[useFormButtonOptions.inject.FORM];
        },
        /**
         * @return {string?}
         */
        action() {
            return this.props?.[useFormButtonOptions.props.ACTION] ?? null;
        },
        /**
         * @return {boolean}
         */
        isFormResetAvailable() {
            const { form } = this;
            if (form == null) {
                return true;
            }
            return form.context.isResetAvailable;
        },
        /**
         * @return {boolean}
         */
        isFormSubmitAvailable() {
            const { form } = this;
            if (form == null) {
                return true;
            }
            return form.context.isSubmitAvailable;
        },
        /**
         * @return {boolean}
         */
        isFormLoading() {
            const { form } = this;
            if (form == null) {
                return false;
            }
            return form.context.isLoading;
        },
        /**
         * @return {boolean}
         */
        hasFormErrors() {
            const { form } = this;
            if (form == null) {
                return false;
            }
            return form.context.hasErrors;
        }
    },
    methods: {
        /**
         * Sets model value
         */
        setValue(field, value) {
            const { form } = this;
            if (!form) {
                return;
            }
            form.setValue(field, value);
        },
        /**
         * Submit form invoker fn
         */
        submit() {
            const { form } = this;
            if (!form) {
                return;
            }
            return form.submit();
        },
        /**
         * Cancel form invoker fn
         */
        cancel() {
            const { form } = this;
            if (!form) {
                return;
            }
            return form.cancel();
        },
        /**
         * Reset form invoker fn
         */
        reset() {
            const { form } = this;
            if (!form) {
                return;
            }
            return form.reset();
        },
        async runFormAction() {
            const { action } = this;
            const methods = {
                [FormAction.CANCEL]: this.cancel,
                [FormAction.RESET]: this.reset,
                [FormAction.SUBMIT]: this.submit
            };
            const fn = methods[action] ?? null;
            if (fn) {
                return await fn();
            }
        }
    }
});

/**
 * Form-button descriptor factory
 * @param {() => object} descriptor         custom descriptor factory to merge with
 * @return {() => object}
 */
export const useFormButtonDescriptor = (descriptor) => () =>
    merge(
        {
            props: {
                [useFormButtonOptions.props.ACTION]: {
                    type: String,
                    default: null,
                    label: 'Действие',
                    options: Object.values(FormAction).map((value) => ({ value, label: value }))
                },
                [useFormButtonOptions.props.LABEL]: {
                    type: String,
                    default: null,
                    label: 'Название'
                }
            }
        },
        descriptor()
    );

/**
 * Form button panel factory
 * @return {() => () => Promise.<import('vue').AsyncComponent>}
 */
export const useFormButtonPanel = () => () => import('../panels/FormButtonPanel.vue');
